import { FC, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../hooks';
import { IDepartment } from 'types';
import {
  IWorkerSelectProps,
  IWorkersSelectReturn,
} from './workers-select.types';
import { mapToSelectOptions, Select } from 'components/ui';
import { Wrapper } from './workers-select.styled';

/**
 * Компонент WorkersSelect.
 * Отображает три выпадающих списка (Отдел, Должность, Сотрудник) для выбора работников.
 * При изменении одного из селекторов, обновляются доступные значения в других селекторах.
 */
export const WorkersSelect: FC<IWorkerSelectProps> = observer(
  ({
    selectedValues,
    onSelect,
    withDissmissed = false,
    includeNoAccess = false,
    withoutCurrentUser = false,
    excludeWorkers = [],
    disabled = false,
    multi = true,
    closeOnSelect = false,
    direction = 'column',
    contained = false,
  }): JSX.Element => {
    const { workers, user } = useStore();

    const currentDepartments = workers.departments;
    const currentPositions = currentDepartments.flatMap((department) =>
      department.positions.map((position) => ({
        ...position,
        departmentId: department.id,
        workers: position.workers.map((worker) => ({
          ...worker,
          positionId: position.id,
        })),
      }))
    );
    const currentWorkers = currentPositions.flatMap((p) => p.workers);

    const availablePositions = useMemo(() => {
      return currentPositions.filter((pos) =>
        selectedValues.departments.includes(pos.departmentId)
      );
    }, [selectedValues.departments, currentPositions]);

    const availableWorkers = useMemo(() => {
      let result = currentWorkers.filter((worker) =>
        selectedValues.positions.includes(worker.positionId)
      );

      if (!withDissmissed) {
        result = result.filter((w) => !w.is_dismiss);
      }

      if (!includeNoAccess) {
        result = result.filter((w) => w.has_access);
      }

      if (withoutCurrentUser) {
        result = result.filter((w) => w.id !== user.id);
      }

      if (excludeWorkers.length) {
        result = result.filter((w) => !excludeWorkers.includes(w.id));
      }

      return result;
    }, [
      currentWorkers,
      selectedValues.positions,
      withDissmissed,
      includeNoAccess,
      withoutCurrentUser,
      user.id,
      excludeWorkers,
    ]);

    const handleChange = (
      key: keyof IWorkersSelectReturn,
      values: string[] | string | null
    ) => {
      let transformValues: string[] = [];

      if (values) {
        transformValues = Array.isArray(values) ? values : [values];
      }

      const newState: IWorkersSelectReturn = {
        departments: selectedValues.departments,
        positions: selectedValues.positions,
        workers: selectedValues.workers,
      };

      if (key === 'departments') {
        newState.departments = transformValues;

        // отфильтровываем позиции, которые относятся к новым департаментам
        const validPositions = currentPositions
          .filter((pos) => transformValues.includes(pos.departmentId))
          .map((pos) => pos.id);

        newState.positions = selectedValues.positions.filter((pid) =>
          validPositions.includes(pid)
        );

        // отфильтровываем сотрудников, которые относятся к новым позициям
        const validWorkers = currentWorkers
          .filter((w) => newState.positions.includes(w.positionId))
          .map((w) => w.id);

        newState.workers = selectedValues.workers.filter((wid) =>
          validWorkers.includes(wid)
        );
      }

      if (key === 'positions') {
        newState.positions = transformValues;

        const validWorkers = currentWorkers
          .filter((w) => transformValues.includes(w.positionId))
          .map((w) => w.id);

        newState.workers = selectedValues.workers.filter((wid) =>
          validWorkers.includes(wid)
        );
      }

      if (key === 'workers') {
        newState.workers = transformValues;
      }

      onSelect(newState);
    };

    // В single-режиме closeOnSelect не передаётся: список закрывается всегда
    const getModeProps = (key: keyof IWorkersSelectReturn) => {
      const onChange = (values: string[] | string | null) =>
        handleChange(key, values);

      return multi
        ? {
            multi: true as const,
            closeOnSelect,
            value: selectedValues[key],
            onChange,
          }
        : { value: selectedValues[key][0], onChange };
    };

    return (
      <Wrapper direction={direction} $contained={contained}>
        <Select
          contained={contained}
          closeOnScroll
          placeholder="Отдел"
          noOptionsMessage="Нет отделов"
          loading={workers.statuses.getDepartmentsPublic.pending}
          disabled={!workers.departments.length || disabled}
          options={mapToSelectOptions(
            currentDepartments as Array<IDepartment>,
            'name',
            'id'
          )}
          {...getModeProps('departments')}
        />
        <Select
          contained={contained}
          closeOnScroll
          placeholder="Должность"
          noOptionsMessage="Нет должностей"
          loading={workers.statuses.getDepartmentsPublic.pending}
          disabled={!selectedValues.departments.length || disabled}
          options={mapToSelectOptions(availablePositions, 'name', 'id')}
          {...getModeProps('positions')}
        />
        <Select
          contained={contained}
          closeOnScroll
          placeholder="Сотрудник"
          noOptionsMessage="Нет сотрудников"
          loading={workers.statuses.getDepartmentsPublic.pending}
          disabled={!selectedValues.positions.length || disabled}
          options={mapToSelectOptions(availableWorkers, 'str_name', 'id')}
          {...getModeProps('workers')}
        />
      </Wrapper>
    );
  }
);
